/**
 * The assessment strip of a hypothesis as a step chart over time (ADR-0095): each assessment
 * holds from its own date to the next one's (the last to the end: the closing date or now), its
 * colour is its verdict and its height its confidence. The geometry and the token classes live
 * here, apart from the markup.
 */
import type { ComponentProps } from 'react'
import type { Badge } from '@/components/ui/badge'
import type {
  AssessmentPoint,
  HypothesisConfidence,
  HypothesisVerdict,
} from '@/types/analytic-hypothesis'

/** One step: the assessment and its share of the strip's width (0..1, all shares sum to 1). */
export interface StripStep {
  point: AssessmentPoint
  share: number
}

/** The narrowest a step gets, so an assessment soon replaced still shows. */
export const MIN_STEP_SHARE = 0.06

/** Steps oldest first; ``points`` must already be oldest first (as the server sends them). */
export function stripSteps(
  points: readonly AssessmentPoint[],
  end: string,
  minShare = MIN_STEP_SHARE
): StripStep[] {
  if (points.length === 0) return []
  const times = points.map(point => Date.parse(point.created_at))
  const last = Math.max(Date.parse(end), times[times.length - 1])
  const span = last - times[0]
  const raw = times.map((time, i) => {
    if (span <= 0) return 1 / points.length
    const next = i + 1 < times.length ? times[i + 1] : last
    return Math.max((next - time) / span, minShare)
  })
  const total = raw.reduce((sum, share) => sum + share, 0)
  return points.map((point, i) => ({ point, share: raw[i] / total }))
}

/** The fill of a step: a theme token per verdict. */
export const VERDICT_FILL: Record<HypothesisVerdict, string> = {
  supported: 'bg-success',
  refuted: 'bg-destructive',
  inconclusive: 'bg-warning',
}

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>['variant']>

/** The badge of a verdict. */
export const VERDICT_BADGE: Record<HypothesisVerdict, BadgeVariant> = {
  supported: 'success',
  refuted: 'destructive',
  inconclusive: 'warning',
}

/** The height of a step: a third per confidence level. */
export const CONFIDENCE_HEIGHT: Record<HypothesisConfidence, string> = {
  low: 'h-1/3',
  medium: 'h-2/3',
  high: 'h-full',
}
